const SAFE_MODE_KEY = "hrw.racing-safe-mode.v1";
const SAFE_MODE_COUNT_KEY = "hrw.racing-safe-mode.count.v1";
const SAFE_MODE_PARAM = "hrw-safe-mode";
const SAFE_MODE_STABLE_MS = 5 * 60_000;
// A second activation means the 3D player has taken the web process down
// twice: stay on the lightweight player until the user explicitly retries,
// instead of re-entering 3D every five minutes (or on every app relaunch)
// and crashing again — the "restart loop" as players experience it.
const SAFE_MODE_STICKY_AFTER = 2;

/** Safe-mode state must outlive the web process (and the app launch) that
 * crashed, so it prefers localStorage; sessionStorage is the fallback for
 * hardened WKWebView sessions where the durable store is unavailable. */
export function durableStorage(target: Window = window): Storage | undefined {
  for (const key of ["localStorage", "sessionStorage"] as const) {
    try {
      const storage = target[key];
      const probe = "hrw.storage-probe";
      storage.setItem(probe, "1");
      storage.removeItem(probe);
      return storage;
    } catch {
      // Hardened WebViews may reject either the getter or writes. Recovery
      // must still activate in the current document, never throw recursively.
    }
  }
  return undefined;
}

export type ClientIncidentCode =
  | "js_exception"
  | "unhandled_rejection"
  | "react_error_boundary"
  | "webgl_context_lost"
  | "webgl_restore_failed"
  | "chunk_recovery_failed";

type TauriInvoke = (
  command: string,
  args?: Record<string, unknown>,
) => Promise<unknown>;

function tauriInvoke(target: Window = window): TauriInvoke | null {
  const internals = (
    target as Window & {
      __TAURI_INTERNALS__?: { invoke?: TauriInvoke };
    }
  ).__TAURI_INTERNALS__;
  return typeof internals?.invoke === "function" ? internals.invoke : null;
}

export function reportClientIncident(
  code: ClientIncidentCode,
  target: Window = window,
): void {
  const invoke = tauriInvoke(target);
  if (!invoke) return;
  void invoke("report_client_incident", { code }).catch(() => undefined);
}

export function activateRacingSafeMode(
  storage: Storage | undefined = durableStorage(),
  now = Date.now(),
): void {
  document.documentElement.dataset.racingSafeMode = "true";
  try {
    const count = Number(storage?.getItem(SAFE_MODE_COUNT_KEY)) || 0;
    storage?.setItem(SAFE_MODE_KEY, String(now));
    storage?.setItem(SAFE_MODE_COUNT_KEY, String(count + 1));
  } catch {
    // The current document remains protected even when persistence fails.
  }
}

/** The user's explicit "Retry 3D": clears the window AND the escalation. */
export function clearRacingSafeMode(
  storage: Storage | undefined = durableStorage(),
): void {
  document.documentElement.dataset.racingSafeMode = "false";
  try {
    storage?.removeItem(SAFE_MODE_KEY);
    storage?.removeItem(SAFE_MODE_COUNT_KEY);
  } catch {
    // Explicit retry still works when storage becomes unavailable.
  }
}

export function isRacingSafeMode(
  storage: Storage | undefined = durableStorage(),
  location: Pick<Location, "search"> = window.location,
  now = Date.now(),
): boolean {
  const params = new URLSearchParams(location.search);
  if (params.get(SAFE_MODE_PARAM) === "1") {
    activateRacingSafeMode(storage, now);
    return true;
  }
  if (
    (Number(storage?.getItem(SAFE_MODE_COUNT_KEY)) || 0) >=
    SAFE_MODE_STICKY_AFTER
  ) {
    return true;
  }
  const activatedAt = Number(storage?.getItem(SAFE_MODE_KEY));
  return (
    Number.isFinite(activatedAt) &&
    activatedAt > 0 &&
    now - activatedAt < SAFE_MODE_STABLE_MS
  );
}

export function installNativeRecovery(
  target: Window = window,
  root: HTMLElement = document.documentElement,
): () => void {
  let lastCode = "";
  let lastAt = 0;
  const report = (code: ClientIncidentCode) => {
    const now = Date.now();
    if (code === lastCode && now - lastAt < 60_000) return;
    lastCode = code;
    lastAt = now;
    reportClientIncident(code, target);
  };
  const onError = () => report("js_exception");
  const onRejection = () => report("unhandled_rejection");
  target.addEventListener("error", onError);
  target.addEventListener("unhandledrejection", onRejection);

  let safe: boolean;
  try {
    safe = isRacingSafeMode(durableStorage(target), target.location);
  } catch {
    safe =
      new URLSearchParams(target.location.search).get(SAFE_MODE_PARAM) === "1";
  }
  root.dataset.racingSafeMode = safe ? "true" : "false";
  if (safe && target.location.search.includes(`${SAFE_MODE_PARAM}=1`)) {
    const clean = new URL(target.location.href);
    clean.searchParams.delete(SAFE_MODE_PARAM);
    target.history.replaceState(target.history.state, "", clean);
  }

  return () => {
    target.removeEventListener("error", onError);
    target.removeEventListener("unhandledrejection", onRejection);
  };
}

export function racingSafeModeActive(): boolean {
  return document.documentElement.dataset.racingSafeMode === "true";
}
